import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import {
  setCurrentSession,
  setMessages,
  addMessage,
  setSelectedDocumentId,
  setError,
  setConnectionStatus,
  clearChat,
  clearSession,
  updateUI,
  setIsSending,
  clearMessages,
} from "@/store/slices/chatSlice";
import { chatApi, documentApi } from "@/services/api/endpoints";
import { Message, ChatSession } from "@/types/index";
import { logger } from "@/utils/logger";
import { useRateLimit } from "./useRateLimit";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { useAsyncDebounce } from "./useDebounce";
import { useHookSafety, getReduxState, safeDispatch } from "@/utils/hookUtils";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { api } from "@/lib/api";

const defaultState = {
  messages: [],
  currentSession: null,
  connectionStatus: "disconnected" as const,
  error: null,
  isSending: false,
  selectedDocumentId: null,
  settings: {
    theme: "light" as const,
    notifications: true,
    soundEnabled: true,
  },
  ui: {
    isSidebarOpen: true,
    selectedTab: "chat" as "chat" | "history" | "settings",
  },
};

interface UseChatReduxReturn {
  messages: Message[];
  currentSession: ChatSession | null;
  connectionStatus: string;
  selectedDocumentId: string | null;
  error: string | null;
  ui: typeof defaultState.ui;
  loading: boolean;
  isSending: boolean;
  sendChatMessage: (content: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: Message) => void;
  fetchSession: (documentId: string) => Promise<ChatSession | null>;
  debouncedFetchSession: (documentId: string) => Promise<ChatSession | null>;
  closeSession: () => Promise<void>;
  loadMessages: (sessionId: string, documentId: string) => Promise<void>;
  updateUI: (ui: Partial<typeof defaultState.ui>) => void;
  clearChat: () => void;
  setSelectedDocumentIdFromHook: (id: string | null) => void;
  handleSelectDocument: (documentId: string) => void;
}

export const useChatRedux = (sessionId?: string): UseChatReduxReturn => {
  // ✅ SEGURANÇA: Validar hook call
  useHookSafety('useChatRedux', 'Chat8');
  
  // ✅ DEBUG: Log hook initialization - LOG SIMPLES PARA TESTE
  // console.log(`[useChatRedux] 🔧 Hook inicializado - ${new Date().toISOString()}`);
  logger.info(`[useChatRedux] 🔧 Hook inicializado`, {
    sessionId,
    hasSessionId: !!sessionId,
    timestamp: new Date().toISOString()
  });

  const dispatch = useDispatch();
  const chatState = useSelector((state: RootState) => state.chat);
  const isClosingRef = useRef(false);
  const isSendingRef = useRef(false);
  const { currentSession } = useSelector((state: RootState) => state.chat);
  const { selectedDocumentId } = useSelector((state: RootState) => state.chat);
  const currentSessionRef = useRef<ChatSession | null>(null);

  // Garante sessionId estável
  const sessionIdRef = useRef<string | undefined>(undefined);

  // Atualiza o ref quando a sessão muda
  useEffect(() => {
    const id = chatState.currentSession?.id?.toString();
    sessionIdRef.current = id && id.length > 0 ? id : undefined;
  }, [chatState.currentSession?.id]);

  // Obter contexto WebSocket de forma segura
  const websocketContext = useWebSocket();

  // Garantir que websocketContext não seja null antes de destructuring
  const { isConnected, sendMessage: sendWebSocketMessage } =
    websocketContext || {
      isConnected: false,
      sendMessage: () => {
        console.warn("[useChatRedux] WebSocket not available");
      },
      isReconnecting: false,
      error: null,
    };

  // Atualiza status da conexão
  useEffect(() => {
    dispatch(setConnectionStatus(isConnected ? "connected" : "disconnected"));
  }, [isConnected, dispatch]);

  // Função de fechamento de sessão - memoizada sem dependências instáveis
  const closeSession = useCallback(async () => {
    // ✅ SEGURANÇA: Usar getReduxState em vez de useSelector em callback
    const currentState = getReduxState();
    const currentSession = currentState?.chat?.currentSession;
    
    if (!currentSession?.id || isClosingRef.current) {
      return;
    }

    const sessionId = currentSession.id.toString();
    const documentId = currentSession.document_id;

    if (!documentId) {
      return;
    }

    try {
      isClosingRef.current = true;
      // ✅ SEGURANÇA: Usar safeDispatch em vez de dispatch em callback
      safeDispatch(setConnectionStatus("disconnected"));

      // Usar a nova função de fechamento seguro
      await chatApi.closeSessionIfNeeded(sessionId, documentId, false);
      safeDispatch(clearSession());
    } catch (error: unknown) {
      const apiError = error as { response?: { status: number; data: unknown } };
      if (apiError.response?.status === 400) {
        // Status 400 é esperado, não fazer nada
      } else {
        safeDispatch(setError(error instanceof Error ? error.message : "Unknown error"));
      }
    } finally {
      isClosingRef.current = false;
    }
  }, []); // Removidas dependências instáveis

  const sendChatMessage = useCallback(
    async (content: string) => {
      if (!sessionIdRef.current) {
        return;
      }

      try {
        // ✅ SEGURANÇA: Usar getReduxState para obter estado atual
        const currentState = getReduxState();
        const selectedDocId = currentState?.chat?.selectedDocumentId;

        const userMessage: Message = {
          id: Date.now().toString(),
          content,
          role: "user",
          timestamp: new Date().toISOString(),
          status: "sending",
          session_id: sessionIdRef.current,
          document_id: selectedDocId || "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          sender: { id: "user", type: "user", name: "Usuário" },
        };

        safeDispatch(addMessage(userMessage));

        const response = await chatApi.sendMessage(
          sessionIdRef.current,
          content,
        );

        // Adicionar a resposta do assistente se existir
        if (response) {
          // Converter para o tipo correto se necessário
          const assistantMessage: Message = {
            id: response.id,
            content: response.content,
            role: isSystemRole(response.role) ? "assistant" : response.role,
            timestamp: response.timestamp || response.created_at,
            status: response.status || "sent",
            session_id: response.session_id,
            document_id: response.document_id || selectedDocId || "",
            created_at: response.created_at,
            updated_at: response.updated_at,
            sender: response.sender
              ? {
                  id: response.sender.id,
                  type: isSystemRole(response.sender.type)
                    ? "assistant"
                    : response.sender.type,
                  name: response.sender.name,
                }
              : { id: "assistant", type: "assistant", name: "Assistente" },
          };
          safeDispatch(addMessage(assistantMessage));
        }
      } catch (error) {
        safeDispatch(
          setError(
            error instanceof Error ? error.message : "Failed to send message",
          ),
        );
      }
    },
    [], // Removidas dependências instáveis
  );

  // Função utilitária para normalizar mensagens do histórico
  function normalizeMessage(msg: unknown): Message {
    // Validação básica
    if (!msg || typeof msg !== 'object') {
      return {
        id: Date.now().toString(),
        content: "Mensagem inválida",
        role: "assistant",
        timestamp: new Date().toISOString(),
        status: "error",
        session_id: "",
        document_id: "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sender: { id: "assistant", type: "assistant", name: "Assistente" },
        metadata: {},
      };
    }

    const msgObj = msg as Record<string, unknown>;

    // Determinar role baseado em sender ou role
    let role: 'user' | 'assistant' = 'assistant';
    if (msgObj.role) {
      role = msgObj.role === 'user' ? 'user' : 'assistant';
    } else if (msgObj.sender) {
      if (typeof msgObj.sender === 'string') {
        role = msgObj.sender === 'bot' ? 'assistant' : 'user';
      } else if (typeof msgObj.sender === 'object' && msgObj.sender && 'type' in msgObj.sender) {
        const senderType = (msgObj.sender as Record<string, unknown>).type;
        role = senderType === 'user' ? 'user' : 'assistant';
      }
    }

    // Determinar sender object
    let sender: { id: string; type: 'user' | 'assistant'; name?: string };
    if (typeof msgObj.sender === 'string') {
      sender = msgObj.sender === 'bot' 
        ? { id: "bot", type: "assistant", name: "Assistente" }
        : { id: "user", type: "user", name: "Você" };
    } else if (typeof msgObj.sender === 'object' && msgObj.sender) {
      const senderObj = msgObj.sender as Record<string, unknown>;
      sender = {
        id: (senderObj.id as string) || (senderObj.type === 'user' ? 'user' : 'assistant'),
        type: (senderObj.type as 'user' | 'assistant') || (senderObj.id === 'user' ? 'user' : 'assistant'),
        name: (senderObj.name as string) || (senderObj.type === 'user' ? 'Você' : 'Assistente'),
      };
    } else {
      sender = { id: "assistant", type: "assistant", name: "Assistente" };
    }

    const normalizedMessage: Message = {
      id: (msgObj.id as string) || Date.now().toString(),
      content: (msgObj.content as string) || (msgObj.message as string) || "",
      role,
      timestamp: (msgObj.timestamp as string) || (msgObj.created_at as string) || new Date().toISOString(),
      status: (msgObj.status as "error" | "pending" | "sending" | "sent") || "sent",
      session_id: (msgObj.session_id as string) || "",
      document_id: (msgObj.document_id as string) || (msgObj.document as string) || "",
      created_at: (msgObj.created_at as string) || new Date().toISOString(),
      updated_at: (msgObj.updated_at as string) || new Date().toISOString(),
      sender,
      metadata: (msgObj.metadata as Record<string, unknown>) || {},
    };

    return normalizedMessage;
  }

  const loadMessages = useCallback(
    async (sessionId: string, documentId: string) => {
      try {
        logger.info(`[loadMessages] 🚀 INICIANDO CARREGAMENTO DE MENSAGENS`);
        logger.info(`[loadMessages] 📋 Parâmetros:`);
        logger.info(`  - sessionId: ${sessionId}`);
        logger.info(`  - documentId: ${documentId}`);
        logger.info(`  - chatState.messages.length: ${chatState.messages.length}`);

        const response = await chatApi.getSessionMessages(
          sessionId,
          documentId,
        );

        logger.info(`[loadMessages] ✅ Resposta da API recebida:`);
        logger.info(`  - response.results.length: ${response.results?.length || 0}`);
        logger.info(`  - response.count: ${response.count || 0}`);

        // Normalizar mensagens para garantir compatibilidade
        const convertedMessages: Message[] = (response.results || []).map(normalizeMessage);

        logger.info(`[loadMessages] 📝 Mensagens normalizadas:`);
        logger.info(`  - convertedMessages.length: ${convertedMessages.length}`);
        logger.info(`  - Primeira mensagem:`, convertedMessages[0]);

        // CRÍTICO: Adicionar mensagens em vez de substituí-las
        // Isso preserva as mensagens do WebSocket que foram sincronizadas

        // Verificar mensagens existentes para evitar duplicação
        const existingMessageIds = new Set(chatState.messages.map(msg => msg.id));
        
        logger.info(`[loadMessages] 🔍 Verificando duplicatas:`);
        logger.info(`  - existingMessageIds.size: ${existingMessageIds.size}`);
        logger.info(`  - existingMessageIds:`, Array.from(existingMessageIds));
        
        // Adicionar apenas mensagens que não existem
        const newMessages = convertedMessages.filter(message => !existingMessageIds.has(message.id));

        logger.info(`[loadMessages] 📤 Mensagens novas para adicionar:`);
        logger.info(`  - newMessages.length: ${newMessages.length}`);

        // Adicionar cada mensagem nova individualmente
        newMessages.forEach((message) => {
          logger.info(`[loadMessages] ➕ Adicionando mensagem: ${message.id} - ${message.content?.substring(0, 50)}...`);
          dispatch(addMessage(message));
        });

        logger.info(`[loadMessages] ✅ CARREGAMENTO CONCLUÍDO`);
        logger.info(`  - Mensagens adicionadas: ${newMessages.length}`);
        logger.info(`  - Total de mensagens no Redux: ${chatState.messages.length + newMessages.length}`);

      } catch (error) {
        logger.error(`[loadMessages] ❌ ERRO AO CARREGAR MENSAGENS:`, error);
        dispatch(
          setError(
            error instanceof Error
              ? error.message
              : "Erro ao carregar mensagens",
          ),
        );
        throw error;
      }
    },
    [dispatch, chatState.messages],
  );

  const { execute } = useRateLimit();

  const fetchSession = useCallback(
    async (documentId: string) => {
      // ✅ VALIDAÇÃO CRÍTICA: Verificar se documentId é válido
      if (!documentId || documentId.trim() === "" || documentId === "undefined" || documentId === undefined) {
        logger.error(`[fetchSession] ❌ Document ID inválido: ${documentId}`);
        throw new Error(`Document ID inválido: ${documentId}`);
      }

      logger.info(`[fetchSession] 🚀 INICIANDO busca de sessão para documento: ${documentId}`);

      // Use refs to get current values without creating dependencies
      const currentSession = chatState.currentSession;

      try {
        // Verificar se já temos a sessão correta
        if (currentSession?.id === documentId && currentSession?.status === 'active') {
          logger.info(`[fetchSession] ✅ Sessão já correta e ativa: ${currentSession.id} = ${documentId}`);
          return currentSession; // SAIR: Não buscar novamente
        }

        // ✅ REGRA DE NEGÓCIO: sessionId = documentId
        // Sempre usar documentId como sessionId para garantir consistência
        const sessionId = documentId;

        logger.info(`[fetchSession] 🔍 Buscando sessão: ${sessionId}`);
        const session = await chatApi.getSession(sessionId);

        if (session) {
          logger.info(`[fetchSession] ✅ Sessão encontrada:`, {
            sessionId: session.id,
            status: session.status
          });

          // ✅ ATUALIZAÇÃO CONDICIONAL: Só atualizar se for diferente
          if (currentSession?.id !== session.id) {
            logger.info(`[fetchSession] 🔄 Atualizando Redux com sessão: ${session.id}`);
            dispatch(setCurrentSession(session));
          } else {
            logger.info(`[fetchSession] ✅ Sessão já está no Redux: ${session.id}`);
          }

          logger.info(`[fetchSession] ✅ Sessão carregada com sucesso: ${session.id}`);
          return session;
        } else {
          logger.error(`[fetchSession] ❌ Sessão não encontrada para: ${sessionId}`);
          throw new Error(`Sessão não encontrada para: ${sessionId}`);
        }
      } catch (error) {
        logger.error(`[fetchSession] ❌ Erro ao buscar sessão:`, error);
        throw error;
      }
    },
    [chatState.currentSession, dispatch]
  );

  // Versão debounced da busca de sessão
  const debouncedFetchSession = useAsyncDebounce(
    fetchSession,
    300,
  );

  // Update ref when session changes
  useEffect(() => {
    currentSessionRef.current = chatState.currentSession;
  }, [chatState.currentSession]);

  // Cleanup on unmount - sem dependências para evitar re-criação
  useEffect(() => {
    return () => {
      // Não fechar sessão automaticamente no unmount
      // A sessão será mantida ativa para permitir reconexão
    };
  }, []); // Empty dependency array for true cleanup

  // ============================================================================
  // EFFECT CRÍTICO: CARREGAR MENSAGENS QUANDO SESSÃO MUDA
  // ============================================================================
  /**
   * useEffect para currentSession
   *
   * ✅ SIMPLIFICAÇÃO: Fluxo hierárquico e previsível
   * Responsabilidades:
   * - Carregar mensagens quando nova sessão é criada
   * - Garantir que histórico seja carregado automaticamente
   * - Sincronizar mensagens com a sessão atual
   *
   * Fluxo:
   * 1. currentSession muda → carregar mensagens
   * 2. Mensagens carregadas → atualizar Redux
   * 3. Componentes re-renderizam com histórico
   *
   * Dependências: currentSession?.id, selectedDocumentId, loadMessages
   */
  useEffect(() => {
    logger.info(`[useChatRedux] 🔄 useEffect currentSession executado`);
    logger.info(`[useChatRedux] 📋 Estado atual:`);
    logger.info(`  - currentSession?.id: ${chatState.currentSession?.id}`);
    logger.info(`  - selectedDocumentId: ${chatState.selectedDocumentId}`);
    logger.info(`  - hasCurrentSession: ${!!chatState.currentSession}`);
    logger.info(`  - currentSessionStatus: ${chatState.currentSession?.status}`);

    if (chatState.currentSession?.id && chatState.selectedDocumentId) {
      logger.info(`[useChatRedux] 🚀 CARREGANDO MENSAGENS - Sessão: ${chatState.currentSession.id}, Documento: ${chatState.selectedDocumentId}`);
      loadMessages(chatState.currentSession.id, chatState.selectedDocumentId);
    } else {
      logger.info(`[useChatRedux] ⏸️ NÃO CARREGANDO MENSAGENS - Condições não atendidas`);
      logger.info(`  - currentSession?.id: ${chatState.currentSession?.id || 'null'}`);
      logger.info(`  - selectedDocumentId: ${chatState.selectedDocumentId || 'null'}`);
    }
  }, [
    chatState.currentSession,
    chatState.selectedDocumentId,
    loadMessages,
  ]);

  // ============================================================================
  // HOOK DE SINCRONIZAÇÃO SIMPLIFICADO
  // ============================================================================
  /**
   * useEffect para sincronizar selectedDocumentId com sessionStorage
   *
   * ✅ SIMPLIFICAÇÃO: Só sincroniza quando documento muda
   * Responsabilidades:
   * - Salvar selectedDocumentId no sessionStorage
   * - Garantir persistência entre navegações
   * - Evitar sobrescrever dados desnecessariamente
   *
   * Fluxo:
   * 1. selectedDocumentId muda → salvar no sessionStorage
   * 2. Só executa quando realmente necessário
   *
   * Dependências: selectedDocumentId
   */
  useEffect(() => {
    if (chatState.selectedDocumentId) {
      const currentStored = sessionStorage.getItem('painel_selected_document_id');
      if (currentStored !== chatState.selectedDocumentId) {
        logger.info(`[useChatRedux] Salvando documento no sessionStorage: ${chatState.selectedDocumentId}`);
        sessionStorage.setItem('painel_selected_document_id', chatState.selectedDocumentId);
      }
    }
  }, [chatState.selectedDocumentId]);

  // ============================================================================
  // EFFECT CRÍTICO: BUSCAR SESSÃO QUANDO DOCUMENTO MUDA
  // ============================================================================
  /**
   * useEffect para selectedDocumentId
   *
   * ✅ SIMPLIFICAÇÃO: Fluxo hierárquico e previsível
   * Responsabilidades:
   * - Buscar sessão quando documento é selecionado
   * - Garantir que sessionId = documentId (regra de negócio)
   * - Atualizar Redux com a sessão encontrada
   *
   * Fluxo:
   * 1. selectedDocumentId muda → buscar documento
   * 2. Documento encontrado → usar document.session_id como sessionId
   * 3. Buscar sessão → atualizar currentSession no Redux
   * 4. WebSocket conecta automaticamente com sessionId correto
   *
   * Dependências: selectedDocumentId, fetchSession
   */
  
  // ✅ DEBUG: Log useEffect registration
  logger.info(`[useChatRedux] 📝 Registrando useEffect para selectedDocumentId`);
  // console.log(`[useChatRedux] 📝 Registrando useEffect para selectedDocumentId - ${new Date().toISOString()}`);
  
  useEffect(() => {
    // console.log(`[useChatRedux] 🔄 useEffect selectedDocumentId executado - ${new Date().toISOString()}`);
    logger.info(`[useChatRedux] 🔄 useEffect selectedDocumentId executado`);
    logger.info(`[useChatRedux] 🔍 Estado atual:`, {
      selectedDocumentId: chatState.selectedDocumentId,
      currentSessionId: chatState.currentSession?.id,
      hasCurrentSession: !!chatState.currentSession,
      currentSessionStatus: chatState.currentSession?.status
    });
    
    if (chatState.selectedDocumentId) {
      // console.log(`[useChatRedux] 🔍 Documento selecionado: ${chatState.selectedDocumentId} - ${new Date().toISOString()}`);
      logger.info(`[useChatRedux] 🔍 Documento selecionado: ${chatState.selectedDocumentId}`);
      logger.info(`[useChatRedux] 🔍 Sessão atual: ${chatState.currentSession?.id || 'null'}`);
      
      // Sempre buscar sessão quando documento muda
      // Não verificar se já temos a sessão correta - deixar o fetchSession decidir
      fetchSession(chatState.selectedDocumentId);
    }
  }, [chatState.selectedDocumentId, chatState.currentSession, fetchSession]);

  // ============================================================================
  // FUNÇÃO CRÍTICA: SELECIONAR DOCUMENTO
  // ============================================================================
  /**
   * handleSelectDocument - Função crítica para seleção de documentos
   *
   * Responsabilidades:
   * - Atualizar selectedDocumentId no Redux
   * - Sincronizar com sessionStorage
   * - Logar seleção para debugging
   * - Redux automaticamente chama fetchSession via useEffect
   * - fetchSession busca a sessão existente (criada pelo backend)
   *
   * Fluxo:
   * 1. Loga seleção do documento
   * 2. Despacha ação para Redux
   * 3. Redux automaticamente chama fetchSession via useEffect
   * 4. fetchSession busca a sessão existente (document_id = session_id)
   *
   * Dependências: dispatch
   */
  const handleSelectDocument = useCallback(
    (documentId: string) => {
      dispatch(setSelectedDocumentId(documentId));
    },
    [dispatch],
  );

  return {
    messages: chatState.messages,
    currentSession: chatState.currentSession,
    connectionStatus: chatState.connectionStatus,
    selectedDocumentId: chatState.selectedDocumentId,
    error: chatState.error,
    ui: chatState.ui,
    loading: false, // TODO: Implement proper loading state
    isSending: chatState.isSending,
    sendChatMessage,
    sendMessage: sendChatMessage,
    addMessage: (message: Message) => dispatch(addMessage(message)),
    fetchSession,
    debouncedFetchSession,
    closeSession,
    loadMessages,
    updateUI: (ui: Partial<typeof chatState.ui>) => dispatch(updateUI(ui)),
    clearChat: () => dispatch(clearChat()),
    setSelectedDocumentIdFromHook: (id: string | null) =>
      dispatch(setSelectedDocumentId(id)),
    handleSelectDocument,
  };
};

function isSystemRole(role: unknown): role is 'system' {
  return role === 'system';
}
